import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'a11y-settings';

const defaultSettings = {
  fontSize: 0,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  reducedMotion: false,
};

function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
    } catch {
      return defaultSettings;
    }
  });
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const applySettings = useCallback((s) => {
    const html = document.documentElement;

    // Font size
    const base = 100 + s.fontSize * 10;
    html.style.fontSize = base === 100 ? '' : `${base}%`;

    // Class toggles
    html.classList.toggle('a11y-high-contrast', s.highContrast);
    html.classList.toggle('a11y-highlight-links', s.highlightLinks);
    html.classList.toggle('a11y-dyslexia-font', s.dyslexiaFont);
    html.classList.toggle('a11y-reduced-motion', s.reducedMotion);
  }, []);

  useEffect(() => {
    applySettings(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch { /* quota exceeded — ignore */ }
  }, [settings, applySettings]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (
        open &&
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape' && open) setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const update = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  const reset = () => {
    setSettings(defaultSettings);
  };

  const canDecrease = settings.fontSize > -3;
  const canIncrease = settings.fontSize < 5;

  return (
    <div className="a11y-widget">
      <button
        ref={btnRef}
        className="a11y-widget__toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Close accessibility menu' : 'Open accessibility menu'}
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28" aria-hidden="true">
          <path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm9 5H3v2h6.5v4l-2 7h2.2l1.8-6.3L13.3 20h2.2l-2-7V9H20V7Z"/>
        </svg>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="a11y-widget__panel"
          role="dialog"
          aria-label="Accessibility settings"
        >
          <h3 className="a11y-widget__title">Accessibility</h3>

          {/* Font Size */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Font Size</span>
            <div className="a11y-widget__font-controls">
              <button
                className="a11y-widget__font-btn"
                onClick={() => canDecrease && update('fontSize', settings.fontSize - 1)}
                disabled={!canDecrease}
                aria-label="Decrease font size"
              >
                A&minus;
              </button>
              <span className="a11y-widget__font-value">
                {settings.fontSize === 0 ? 'Default' : `${settings.fontSize > 0 ? '+' : ''}${settings.fontSize}`}
              </span>
              <button
                className="a11y-widget__font-btn"
                onClick={() => canIncrease && update('fontSize', settings.fontSize + 1)}
                disabled={!canIncrease}
                aria-label="Increase font size"
              >
                A+
              </button>
            </div>
          </div>

          {/* High Contrast */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">High Contrast</span>
            <button
              className={`a11y-widget__switch ${settings.highContrast ? 'a11y-widget__switch--on' : ''}`}
              role="switch"
              aria-checked={settings.highContrast}
              onClick={() => update('highContrast', !settings.highContrast)}
            >
              <span className="a11y-widget__switch-thumb" />
            </button>
          </div>

          {/* Highlight Links */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Highlight Links</span>
            <button
              className={`a11y-widget__switch ${settings.highlightLinks ? 'a11y-widget__switch--on' : ''}`}
              role="switch"
              aria-checked={settings.highlightLinks}
              onClick={() => update('highlightLinks', !settings.highlightLinks)}
            >
              <span className="a11y-widget__switch-thumb" />
            </button>
          </div>

          {/* Dyslexia Font */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Dyslexia Font</span>
            <button
              className={`a11y-widget__switch ${settings.dyslexiaFont ? 'a11y-widget__switch--on' : ''}`}
              role="switch"
              aria-checked={settings.dyslexiaFont}
              onClick={() => update('dyslexiaFont', !settings.dyslexiaFont)}
            >
              <span className="a11y-widget__switch-thumb" />
            </button>
          </div>

          {/* Reduce Motion */}
          <div className="a11y-widget__option">
            <span className="a11y-widget__label">Reduce Motion</span>
            <button
              className={`a11y-widget__switch ${settings.reducedMotion ? 'a11y-widget__switch--on' : ''}`}
              role="switch"
              aria-checked={settings.reducedMotion}
              onClick={() => update('reducedMotion', !settings.reducedMotion)}
            >
              <span className="a11y-widget__switch-thumb" />
            </button>
          </div>

          {/* Reset */}
          <button className="a11y-widget__reset" onClick={reset}>
            Reset All
          </button>
        </div>
      )}
    </div>
  );
}

export default AccessibilityWidget;
